import { useEffect, useMemo, useState } from 'react';
import type { ArticlePost } from '@/lib/articles';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import Avatar from '@/components/base/Avatar';

interface Props {
  post: ArticlePost;
  liked: boolean;
  likePending?: boolean;
  onLike?: () => Promise<void> | void;
}

const stripCenterMarkers = (content: string) =>
  (content || '').replace(/:::center\s*\n([\s\S]*?)\n:::/g, (_matched, inner: string) => `${inner}`);

const BlogDetailContent = ({ post, liked, likePending = false, onLike }: Props) => {
  const [copied, setCopied] = useState(false);
  const markdownContent = useMemo(
    () => stripCenterMarkers((post.content || '').replace(/\r\n/g, '\n')),
    [post.content]
  );
  const [likeCount, setLikeCount] = useState(post.likes);

  useEffect(() => {
    setLikeCount(post.likes);
  }, [post.likes, post.id]);

  const handleLike = async () => {
    if (liked || likePending) {
      return;
    }
    await onLike?.();
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const normalizeAssetUrl = (src?: string) => {
    if (!src) {
      return '';
    }
    const value = src.trim();
    if (!value) {
      return '';
    }
    if (/^(https?:)?\/\//i.test(value) || /^data:/i.test(value) || /^blob:/i.test(value)) {
      return value;
    }
    if (value.startsWith('/gateway/')) {
      return value;
    }
    if (value.startsWith('/')) {
      return `/gateway${value}`;
    }
    return `/gateway/${value.replace(/^\.?\//, '')}`;
  };

  return (
    <div>
      {/* Tags */}
      <div className="flex flex-wrap gap-2 mb-8">
        {post.tags.map((t) => (
          <span
            key={t}
            className="px-3 py-1.5 bg-slate-100 hover:bg-violet-50 hover:text-violet-700 text-slate-600 text-xs font-medium rounded-full cursor-pointer transition-colors"
          >
            #{t}
          </span>
        ))}
      </div>

      {/* Content */}
      <div className="prose-custom">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={{
            img: ({ src, alt }) => (
              <img
                src={normalizeAssetUrl(typeof src === 'string' ? src : '')}
                alt={alt || ''}
                loading="lazy"
              />
            ),
            a: ({ href, children }) => (
              <a
                href={href}
                target="_blank"
                rel="noreferrer noopener"
              >
                {children}
              </a>
            )
          }}
        >
          {markdownContent}
        </ReactMarkdown>
      </div>

      {/* Action Bar */}
      <div className="mt-10 pt-6 border-t border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={handleLike}
            disabled={liked || likePending}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-all whitespace-nowrap ${
              liked
                ? 'bg-rose-50 text-rose-500 border border-rose-200'
                : 'bg-slate-100 text-slate-600 hover:bg-rose-50 hover:text-rose-500 border border-transparent'
            }`}
          >
            <i className={liked ? 'ri-heart-fill' : 'ri-heart-line'} />
            {liked ? '已按讚' : likePending ? '提交中...' : '覺得有用'} · {likeCount}
          </button>
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200 text-sm font-medium cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className={copied ? 'ri-check-line text-emerald-600' : 'ri-link'} />
            {copied ? '已複製' : '分享連結'}
          </button>
        </div>
        <div className="text-xs text-slate-400">
          發布於 {new Date(post.publishedAt).toLocaleDateString('zh-TW', { year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* Author Card */}
      <div className="mt-8 p-6 bg-slate-50 rounded-2xl border border-slate-100">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">關於作者</p>
        <div className="flex items-start gap-4">
          <Avatar src={post.authorAvatar} alt={post.author} className="w-14 h-14 rounded-xl flex-shrink-0" />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-bold text-[#0A1628]">{post.author}</p>
              <span className="text-xs text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full font-medium">
                {post.authorCompany}
              </span>
            </div>
            <p className="text-xs text-slate-500 mb-3">{post.authorRole} · {post.authorCompany}</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              來自 {post.authorCompany} 的一線從業者，在 AI 職涯領域深耕多年，
              持續為 NexusGo 社群分享來自頂尖外商的真實產業洞察與實戰經驗。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogDetailContent;
